// src/settings/PortalSettings.js
// Portal-wide preferences (superadmin). First up: map theme — a live preview
// map plus a dropdown of dark themes; picking one re-skins every map in the
// portal for everyone (saved server-side, cached locally for instant paint).
import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Map as MapIcon, Check } from 'lucide-react';
import { MAP_THEMES, DEFAULT_MAP_THEME, applyMapTheme, saveMapTheme } from '../lib/portalTheme';

// Somewhere pretty to preview: downtown Sacramento.
const PREVIEW_CENTER = [38.5816, -121.4944];

function PortalSettings() {
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('portal.mapTheme') || DEFAULT_MAP_THEME; } catch (_) { return DEFAULT_MAP_THEME; }
  });
  const [savedTheme, setSavedTheme] = useState(() => {
    try { return localStorage.getItem('portal.mapTheme') || DEFAULT_MAP_THEME; } catch (_) { return DEFAULT_MAP_THEME; }
  });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dirty = theme !== savedTheme;

  // Dropdown = preview only (this device, this session). Save persists.
  const preview = (key) => {
    setTheme(key);
    setSaved(false);
    setError('');
    applyMapTheme(key);
  };
  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await saveMapTheme(theme);
      setSavedTheme(theme);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError('Could not save the theme.');
    } finally { setSaving(false); }
  };
  const revert = () => { setTheme(savedTheme); applyMapTheme(savedTheme); setError(''); };

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-3 space-y-6">
      <div>
        <h1 className="text-3xl max-md:text-xl font-bold tracking-tight">Portal Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Preferences that apply across the whole portal, for every user.</p>
      </div>

      {/* Map theme */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 shadow-2xl p-6 max-md:p-4 max-w-2xl">
        <h2 className="text-lg font-bold flex items-center gap-2 mb-1">
          <MapIcon className="w-4 h-4 text-indigo-400" /> Map Theme
        </h2>
        <p className="text-xs text-zinc-500 mb-4">
          Skins every map in the portal — scheduling, project overviews, customer profiles, bids.
        </p>

        <div className="flex items-center gap-3 flex-wrap mb-4">
          <select
            value={theme}
            onChange={(e) => preview(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-zinc-200 outline-none focus:border-indigo-600/60"
          >
            {Object.entries(MAP_THEMES).map(([key, t]) => (
              <option key={key} value={key}>{t.label}</option>
            ))}
          </select>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              dirty ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-gray-800 text-zinc-600 cursor-not-allowed'
            }`}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {dirty && !saving && (
            <button onClick={revert} className="text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-2">
              Revert preview
            </button>
          )}
          {saved && (
            <span className="text-xs font-semibold text-emerald-400 flex items-center gap-1">
              <Check className="w-3.5 h-3.5" /> Saved portal-wide
            </span>
          )}
          {dirty && !saving && <span className="text-xs text-amber-400/80">Previewing — not saved yet</span>}
          {error && <span className="text-xs text-red-400">{error}</span>}
        </div>

        {/* Live preview — the tiles wear the same CSS variable the whole
            portal uses, so what you see here is exactly what every map gets. */}
        <div className="relative z-0 rounded-xl overflow-hidden border border-gray-800/70 h-52 w-full">
          <style>{`.map-tiles-dark { filter: var(--map-tiles-filter, ${MAP_THEMES[DEFAULT_MAP_THEME].filter}); }`}</style>
          <MapContainer
            center={PREVIEW_CENTER}
            zoom={13}
            style={{ height: '100%', width: '100%', background: '#111827' }}
            zoomControl={false}
            scrollWheelZoom={false}
            attributionControl={false}
          >
            <TileLayer
              className="map-tiles-dark"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors'
            />
            <Marker
              position={PREVIEW_CENTER}
              icon={L.divIcon({
                className: 'custom-marker',
                html: `<div style="background-color:#6366f1;width:16px;height:16px;border-radius:50%;border:2px solid #e5e7eb;box-shadow:0 0 6px rgba(0,0,0,0.6);"></div>`,
                iconSize: [16, 16],
                iconAnchor: [8, 8],
              })}
            />
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

export default PortalSettings;
